import { VFC, useCallback, useMemo, useState } from "react";
import { Empty, Select, SelectProps, Spin } from "antd";
import { DefaultOptionType } from "antd/lib/select";

import { useStoreList } from "../queries/stores";
import { useDebounce } from "../hooks";
import { StoreListPayload } from "../queries/api/stores";
import { Store } from "../queries/api/types";

interface SelectStoreProps extends Omit<SelectProps, "options" | "onSearch"> {
    additionalOptions?: DefaultOptionType[];
    queryPayload?: StoreListPayload;
    disabledStores?: Array<Store["id"]>;
    excludedStores?: Array<Store["id"]>;
    initialValue?: Array<Partial<Store> & { id?: Store["id"]; name?: string }>;
    onStoreSelect?: (store: { id: Store["id"]; reference?: Store["reference"]; name: Store["name"] }) => void;
}

const SelectStore: VFC<SelectStoreProps> = ({
    additionalOptions = [],
    queryPayload,
    disabledStores = [],
    excludedStores = [],
    initialValue = [],
    onStoreSelect,
    ...props
}) => {
    const [query, setQuery] = useState<string | undefined>(undefined);
    const search = useDebounce(query, 500);
    const { data: storeListData, isLoading } = useStoreList({
        pageSize: 20,
        search,
        ...queryPayload,
    });

    // Filter out initialValue items that don't have both valid id and name
    const validInitialValues = useMemo(
        () => (initialValue || []).filter((item) => item.id && item.name),
        [initialValue],
    );

    // Combine initial values with search results for looking up store details
    const allStores = useMemo(
        () => [...validInitialValues, ...(storeListData?.items ?? [])],
        [validInitialValues, storeListData?.items],
    );

    const options: SelectProps["options"] = useMemo(() => {
        return [
            ...validInitialValues, // Display initial value only if valid
            ...(storeListData?.items.filter(
                (store) => !validInitialValues?.map((initialStore) => initialStore.id).includes(store.id),
            ) ?? []), // Search items, excluding initial value
        ]
            .filter(({ id }) => id && !excludedStores.includes(id))
            .map(({ id, reference, name }) => ({
                label: [reference, name].filter(Boolean).join(" - "),
                value: id ?? "",
                disabled: disabledStores.includes(id ?? ""),
            }));
    }, [disabledStores, excludedStores, validInitialValues, storeListData?.items]);

    const handleSelect = useCallback(
        (value: Store["id"]) => {
            setQuery(undefined);
            if (onStoreSelect) {
                const selectedStore = allStores.find((store) => store.id === value);
                if (selectedStore) {
                    onStoreSelect({
                        id: selectedStore.id!,
                        reference: selectedStore.reference,
                        name: selectedStore.name!,
                    });
                }
            }
        },
        [allStores, onStoreSelect],
    );

    return (
        <Select
            options={[...additionalOptions, ...options]}
            showSearch
            onSearch={(query) => setQuery(query)}
            notFoundContent={isLoading ? <Spin size="small" /> : <Empty />}
            filterOption={false}
            onSelect={handleSelect}
            {...props}
        />
    );
};

export default SelectStore;
